import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { withSize } from 'react-sizeme';
import { makeStyles } from '@material-ui/core/styles';
import { Colors, Metrics } from '../../themes';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import SvgIcon from '../../icons/SvgIcon';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Table from '../common/Table';
import { generateId } from '../../lib/Utils';
import moment from 'moment';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`wrapped-tabpanel-${index}`}
      aria-labelledby={`wrapped-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `wrapped-tab-${index}`,
    'aria-controls': `wrapped-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => {
  return {
    DetailsWidget: {
      display: 'flex',
      flexDirection: 'column',
      overflowWrap: 'break-word',
    },
    headerButtonsContainer: {
      background: Colors.navBackgroundGrey,
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      borderRadius: '4px 4px 0 0',
      padding: 10,
      [theme.breakpoints.down(Metrics.minWidth)]: {
        padding: 6,
        justifyContent: 'flex-start',
      },
    },
    button: {
      margin: theme.spacing(2),
      fontSize: 14,
      color: Colors.white,
      border: `1px solid ${Colors.disabledGrey}`,
      '&:hover': {
        background: Colors.disabledGrey,
      },
      '&:disabled': {
        border: `1px solid ${Colors.disabledGrey}`,
        background: Colors.navBackgroundGrey,
        '&:hover': {
          background: Colors.black,
        },
      },
    },
    smallSizeButton: {
      height: 35,
      fontSize: 12,
      paddingLeft: 6,
      paddingRight: 6,
    },
    saveButton: {
      background: Colors.blue,
      border: 'none',
      '&:hover': {
        background: Colors.lightBlue,
      },
    },
    deleteButton: {
      margin: 10,
      marginRight: 'auto',
      minWidth: 38,
      width: 38,
      maxWidth: 38,
      padding: 0,
      borderColor: Colors.red,
      '&:hover': {
        background: Colors.red,
      },
      [theme.breakpoints.down(Metrics.minWidth)]: {
        marginRight: 8,
      },
      '&:disabled:hover': {
        background: Colors.black,
      },
    },
    // details
    detailsContainer: {
      padding: '25px 20px',
      [theme.breakpoints.down('xs')]: {
        padding: 15,
      },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    formContainer: {
      alignSelf: 'center',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
    },
    scheduleBox: {
      marginTop: 20,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    addRowButton: {
      alignSelf: 'flex-end',
      margin: 20,
    },
    weeklyTab: {
      marginBottom: 20,
    },
    yearlyTab: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    errorDisplay: {
      margin: '20px 10px 0 10px',
      color: Colors.errorRed,
    },
  };
});

const ScheduleDetails = (props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { width } = props.size;
  const isSmall = width < Metrics.formDetails.small;
  const formInputWidth = isSmall ? width - 40 : (width - 60) / 2;
  const {
    deleteButtonText,
    add1ButtonText,
    onDeleteButtonClick,
    onAdd1ButtonClick,
    onSaveButtonClick,
    isDeleteButtonDisabled,
    onChange,
    hideDetails,
    name,
    weeklyData,
    changeWeeklyData,
    yearlyData,
    changeYearlyData,
    checkedExceptions,
    changeCheckedExceptions,
    isUntouched,
    setUntouched,
    isNewSchedule,
    scheduleNameId,
  } = props;
  const smallerThanMedium = useMediaQuery(`(max-width: ${Metrics.breakpoints.md}px)`);
  const handleSubmit = (event) => {
    event.preventDefault();
    onSaveButtonClick();
  };
  // tabs
  const [tabValue, setTabValue] = React.useState('weekly');
  useEffect(() => setTabValue('weekly'), [scheduleNameId]);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  // validation
  const [isNameError, setNameError] = useState(false);
  const [nameHelperText, setNameHelperText] = useState(null);
  const [isNameTouched, setNameTouched] = useState(false);
  const [weeklyTimeErrorRowIndexes, setWeeklyTimeErrorRowIndexes] = useState([]);
  const [weeklyTimeErrors, setWeeklyTimeErrors] = useState([]);
  const [yearlyTimeErrorRowIndexes, setYearlyTimeErrorRowIndexes] = useState([]);
  const [yearlyTimeErrors, setYearlyTimeErrors] = useState([]);
  const [isFormValid, setFormValid] = useState(false);

  const setFieldsTouched = useCallback(
    (value) => {
      setNameTouched(value);
      setUntouched(false);
    },
    [setUntouched]
  );

  useEffect(() => {
    const validateName = () => {
      const isNameValid = name !== '';
      if (!isNameValid && !isNameTouched) {
        setFormValid(false);
      }
      if (!isNameValid && isNameTouched) {
        setNameError(true);
        setNameHelperText(t('validation.requiredField'));
        setFormValid(false);
      }
      if (isNameValid) {
        setNameError(false);
        setNameHelperText(null);
      }
    };
    validateName();
  }, [name, isNameTouched, t]);

  useEffect(() => {
    if (isUntouched) {
      setFieldsTouched(false);
      setNameError(false);
      setNameHelperText(null);
    }
  }, [isUntouched, setFieldsTouched]);

  const validateTime = useCallback(
    (data, dataType) => {
      const weekly = dataType === 'weekly';
      const start = weekly ? 'minTime' : 'startTime';
      const end = weekly ? 'maxTime' : 'endTime';
      const setTimeErrorText = weekly ? setWeeklyTimeErrors : setYearlyTimeErrors;
      const setTimeErrorRowIndexes = weekly ? setWeeklyTimeErrorRowIndexes : setYearlyTimeErrorRowIndexes;
      const error1 = t('scheduleManager_error_both_time_fields');
      const error2 = t('scheduleManager_error_end_before_start');
      const getAllIndexesAndSetError = (arr, condition) => {
        let errors = [];
        let errorIndexes = [];
        for (let i = 0; i < arr.length; i++) {
          let errorMessage = condition(arr[i]);
          if (errorMessage) {
            errorIndexes.push(i);
            if (!errors.includes(errorMessage)) {
              errors.push(errorMessage);
            }
          }
          setTimeErrorText(errors);
          setTimeErrorRowIndexes(errorIndexes);
          if (errors.length > 0) {
            setFormValid(false);
          }
        }
      };
      const checkErrors = (schedule) => {
        if ((!schedule[start] && schedule[end]) || (schedule[start] && !schedule[end])) {
          return error1;
        } else if (
          schedule[start] &&
          schedule[end] &&
          moment.utc(schedule[end], 'HH:mm:ss').isBefore(moment.utc(schedule[start], 'HH:mm:ss'))
        ) {
          return error2;
        }
        return null;
      };
      getAllIndexesAndSetError(data, checkErrors);
    },
    [t]
  );

  useEffect(() => validateTime(weeklyData, 'weekly'), [weeklyData, validateTime]);
  useEffect(() => validateTime(yearlyData, 'yearly'), [yearlyData, validateTime]);

  useEffect(() => {
    const validateForm = () => {
      setFormValid(
        !isNameError &&
        weeklyTimeErrors.length === 0 &&
        yearlyTimeErrors.length === 0 &&
        (scheduleNameId || isNewSchedule)
      );
    };
    validateForm();
  }, [weeklyData, yearlyData, weeklyTimeErrors, yearlyTimeErrors, isNameError, isNewSchedule, scheduleNameId]);

  const handleBlur = (event) => {
    const { name } = event.target;
    if (name === 'name') {
      setNameTouched(true);
      setUntouched(false);
    }
  };

  const otherTabErrors =
    tabValue === 'weekly' && yearlyTimeErrors.length > 0
      ? t('scheduleManager_error_annual_tab')
      : tabValue === 'yearly' && weeklyTimeErrors.length > 0
        ? t('scheduleManager_error_weekly_tab')
        : null;
  const timeErrors = tabValue === 'weekly' ? weeklyTimeErrors.slice() : yearlyTimeErrors.slice();
  if (otherTabErrors && !timeErrors.includes(otherTabErrors)) {
    timeErrors.push(otherTabErrors);
  }

  const errorDisplay =
    timeErrors.length > 0 ? (
      <div className={classes.errorDisplay}>
        {timeErrors.map((error, index) => (
          <div key={index}>{error}</div>
        ))}
      </div>
    ) : null;

  const addRow = () =>
    changeYearlyData(
      yearlyData.concat({
        rowId: generateId(),
        exceptionDate: new Date().toJSON(),
        startTime: '00:00:00',
        endTime: '23:59:00',
      })
    );

  const addWeeklyRow = (row) => {
    const rowIndex = weeklyData.findIndex((element) => element.rowId === row.rowId);
    const newObject = {
      rowId: generateId(),
      weekDay: row.weekDay,
      minTime: '00:00:00',
      maxTime: '23:59:00',
      mandatory: false,
      dayName: row.dayName,
    }

    changeWeeklyData([
      ...weeklyData.slice(0, rowIndex + 1),
      newObject,
      ...weeklyData.slice(rowIndex + 1)
    ]
    );
  }
  const removeRow = (row, data) => {
    const changeData = data === weeklyData ? changeWeeklyData : changeYearlyData;
    const rowIndex = data.findIndex((data) => data.rowId === row.rowId);
    const newData = [...data.slice(0, rowIndex), ...data.slice(rowIndex + 1)];
    changeData(newData);
  };

  const selectAllDay = (schedule) => {
    const previousChecked = checkedExceptions;
    //check if the schedule was present in checkedExceptions
    const selectedIndex = previousChecked.findIndex((item) => item.rowId === schedule.rowId);
    const allDaySchedule = { ...schedule, startTime: '00:00:00', endTime: '23:59:00' };
    let newChecked = [];
    if (selectedIndex === -1) {
      // if not found, add it
      newChecked = newChecked.concat(previousChecked, allDaySchedule);
      // update yearlyData to change times to all day
      const indexInYearly = yearlyData.findIndex((item) => item.rowId === schedule.rowId);
      const newYearly = yearlyData.map((item, index) => {
        if (index !== indexInYearly) {
          return item;
        }
        return allDaySchedule;
      });
      changeYearlyData(newYearly);
    } else if (selectedIndex === 0) {
      // if first item, remove it
      newChecked = newChecked.concat(previousChecked.slice(1));
    } else if (selectedIndex === previousChecked.length - 1) {
      // if last item, remove it
      newChecked = newChecked.concat(previousChecked.slice(0, -1));
    } else if (selectedIndex > 0) {
      // if between first and last, remove it
      newChecked = newChecked.concat(previousChecked.slice(0, selectedIndex), previousChecked.slice(selectedIndex + 1));
    }
    changeCheckedExceptions(newChecked);
  };

  const handleChange = (value, row, field, data) => {
    const changeData = data === weeklyData ? changeWeeklyData : changeYearlyData;
    const selectedRow = data.find((schedule) => schedule['rowId'] === row['rowId']);
    const newRow = { ...selectedRow, [field]: value };
    const selectedIndex = data.findIndex((schedule) => schedule['rowId'] === row['rowId']);
    const newSchedules = data.map((schedule, index) => {
      if (index !== selectedIndex) {
        return schedule;
      }
      return newRow;
    });
    changeData(newSchedules);
  };

  return (
    <div className={classes.DetailsWidget}>
      <form
        id="detailsForm"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        onKeyPress={(event) => props.onKeyPressFormSubmit(event)}
      >
        <div className={classes.headerButtonsContainer}>
          <Button
            type="button"
            className={clsx(classes.deleteButton, isSmall && classes.smallSizeButton)}
            variant="outlined"
            aria-label={deleteButtonText}
            onClick={onDeleteButtonClick}
            disabled={isDeleteButtonDisabled}
          >
            <SvgIcon
              icon={'Trash'}
              color={isDeleteButtonDisabled ? Colors.disabledGrey : Colors.white}
              stroke={2}
              width={14}
            />
          </Button>
          <Button
            type="button"
            variant="outlined"
            className={clsx(classes.button, isSmall && classes.smallSizeButton)}
            onClick={onAdd1ButtonClick}
            endIcon={<SvgIcon icon="Add" width={smallerThanMedium ? 16 : 20} color={Colors.white} />}
          >
            {add1ButtonText}{' '}
          </Button>
          <Button
            type="submit"
            variant="contained"
            className={clsx(classes.button, classes.saveButton, isSmall && classes.smallSizeButton)}
            endIcon={
              <SvgIcon
                icon="Save"
                width={smallerThanMedium ? 16 : 20}
                color={!isFormValid ? Colors.disabledGrey : Colors.white}
              />
            }
            disabled={!isFormValid}
            onMouseEnter={() => setFieldsTouched(true)}
          >
            {t('Global_save')}
          </Button>
        </div>
        {!hideDetails && (
          <div className="bodyComponent">
            <div className={classes.detailsContainer}>
              <Typography variant="h2">{t('common.information').toUpperCase()}</Typography>
              <div className={classes.formContainer}>
                <TextField
                  style={{ width: formInputWidth, ...(!isSmall && { marginRight: 10 }) }}
                  id="name"
                  label={t('tagTypeManager_tagTypeInfo_name')}
                  name="name"
                  margin="normal"
                  value={props.name}
                  onChange={onChange}
                  required
                  onBlur={handleBlur}
                  error={isNameError}
                  helperText={nameHelperText}
                />
                <TextField
                  style={{ width: formInputWidth, ...(!isSmall && { marginLeft: 10 }) }}
                  id="description"
                  label={t('tagTypeManager_tagTypeInfo_description')}
                  name="description"
                  margin="normal"
                  value={props.description}
                  onChange={onChange}
                />
              </div>
              <div className={classes.scheduleBox}>
                <Typography className={classes.header2} variant="h2">
                  {t('scheduleManager_schedule_header').toUpperCase()}
                </Typography>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="schedule tabs">
                  <Tab
                    value="weekly"
                    label={t('scheduleManager_weeklySchedule_tab')}
                    wrapped
                    {...a11yProps('weekly')}
                  />
                  <Tab value="yearly" label={t('scheduleManager_yearSchedule_tab')} {...a11yProps('yearly')} />
                </Tabs>
              </div>
            </div>
          </div>
        )}
      </form>
      {!hideDetails && (
        <div className={classes.schedulesTableContainer}>
          <TabPanel className={classes.weeklyTab} value={tabValue} index="weekly">
            <Table
              tableTitle={t('persons.tags')}
              data={props.weeklyData}
              columns={[
                { title: t('scheduleManager_schedule_col1'), field: 'dayName' },
                { title: t('scheduleManager_schedule_col2'), field: 'minTime', type: 'timeInput' },
                { title: t('scheduleManager_schedule_col3'), field: 'maxTime', type: 'timeInput' },
              ]}
              handleChangeTime={(momentTime, row, field) => {
                const time = momentTime ? momentTime.format('HH:mm:ss') : null;
                handleChange(time, row, field, weeklyData);
              }}
              timeErrorRowIndexes={weeklyTimeErrorRowIndexes}
              handleDeleteButtonClick={(row) => {
                removeRow(row, weeklyData);
              }}
              handleAddButtonClick={addWeeklyRow}
              noHeader
              noFooter
              addWeeklyColumn={isNewSchedule}
              deleteWeeklyColumn={isNewSchedule}
            />
            {errorDisplay}
          </TabPanel>
          {/* annual schedule */}
          <TabPanel className={classes.yearlyTab} value={tabValue} index="yearly">
            <Table
              tableTitle={t('persons.tags')}
              data={yearlyData}
              columns={[
                { title: t('scheduleManager_schedule_col1'), field: 'exceptionDate', type: 'dateInput' },
                { title: t('scheduleManager_schedule_col2'), field: 'startTime', type: 'timeInput' },
                { title: t('scheduleManager_schedule_col3'), field: 'endTime', type: 'timeInput' },
              ]}
              handleDeleteButtonClick={(row) => {
                removeRow(row, yearlyData);
              }}
              noDataText={t('doorScheduleManager_dScheduleInfo_noSchedule')}
              checkboxColumn="end"
              checkboxColumnTitle={t('scheduleManager_schedule_col4')}
              checkedArray={checkedExceptions}
              handleClick={(schedule) => selectAllDay(schedule)}
              handleChangeDate={(scheduleDate, row, field) =>
                handleChange(scheduleDate.toJSON(), row, field, yearlyData)
              }
              handleChangeTime={(momentTime, row, field) => {
                const time = momentTime ? momentTime.format('HH:mm:ss') : null;
                handleChange(time, row, field, yearlyData);
              }}
              timeErrorRowIndexes={yearlyTimeErrorRowIndexes}
              disableTimeInputIfChecked
              activeRowField={'rowId'}
              cellPadding
              deleteColumn
              noHeader
              noFooter
            />
            {errorDisplay}
            <Button className={clsx([classes.button, classes.addRowButton])} onClick={addRow}>
              {t('add_row')}
            </Button>
          </TabPanel>
        </div>
      )}
    </div>
  );
};

export default withSize()(ScheduleDetails);
